import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

export interface RadialChoice {
  id: string;
  label: string;
  color: string;
  active?: boolean;
}

interface RadialChoiceMenuProps {
  /** Kept so callers can pass the clicked control; layout uses the dialog. */
  anchorEl?: HTMLElement;
  containerEl: HTMLElement;
  items: RadialChoice[];
  disabled?: boolean;
  onSelect: (id: string) => void;
  onClose: () => void;
}

function contrastInk(hex: string): string {
  const raw = hex.replace('#', '');
  if (raw.length !== 6) return '#fff';
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 165 ? '#111' : '#fff';
}

const DOT = 72;
const LABEL_H = 28;
const ITEM_H = DOT + 8 + LABEL_H;

const RadialChoiceMenu: React.FC<RadialChoiceMenuProps> = ({
  containerEl,
  items,
  disabled,
  onSelect,
  onClose,
}) => {
  const [box, setBox] = useState({ x: 0, y: 0, w: 0, h: 0 });

  useLayoutEffect(() => {
    const update = () => {
      const c = containerEl.getBoundingClientRect();
      setBox({ x: c.width / 2, y: c.height / 2, w: c.width, h: c.height });
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(containerEl);
    window.addEventListener('resize', update);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [containerEl]);

  const radius = useMemo(() => {
    const pad = ITEM_H / 2 + 16;
    const maxR = Math.max(120, Math.min(box.w, box.h) / 2 - pad);
    const desired = Math.max(150, 70 + items.length * 16);
    return Math.min(desired, maxR);
  }, [items.length, box]);

  const pick = (event: React.PointerEvent | React.MouseEvent, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    onSelect(id);
  };

  return (
    <Box
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        bgcolor: 'rgba(8, 12, 16, 0.28)',
        pointerEvents: 'auto',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: box.x,
          top: box.y,
          width: radius * 2 + DOT + 56,
          height: radius * 2 + DOT + 56,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          bgcolor: 'rgba(16, 20, 26, 0.72)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.14)',
          boxShadow: '0 20px 56px rgba(0,0,0,0.45)',
          pointerEvents: 'none',
        }}
      />
      {items.map((item, index) => {
        const angle = (Math.PI * 2 * index) / items.length - Math.PI / 2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        return (
          <Box
            key={item.id || `${item.label}-${index}`}
            component="button"
            type="button"
            disabled={disabled}
            aria-label={item.label}
            aria-pressed={item.active}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => pick(event, item.id)}
            sx={{
              position: 'absolute',
              left: box.x,
              top: box.y,
              transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.75,
              zIndex: 3,
              border: 0,
              p: 0,
              m: 0,
              bgcolor: 'transparent',
              cursor: disabled ? 'default' : 'pointer',
              font: 'inherit',
              color: 'inherit',
              WebkitTapHighlightColor: 'transparent',
              '&:disabled': { cursor: 'default', opacity: 0.55 },
            }}
          >
            <Box
              sx={{
                width: DOT,
                height: DOT,
                borderRadius: '50%',
                border: item.active ? '3px solid #fff' : '2px solid rgba(255,255,255,0.35)',
                bgcolor: item.color,
                color: contrastInk(item.color),
                boxShadow: item.active
                  ? `0 0 0 3px ${item.color}, 0 8px 20px rgba(0,0,0,0.45)`
                  : '0 8px 18px rgba(0,0,0,0.4)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1.2,
                textAlign: 'center',
                maxWidth: 110,
                px: 1,
                py: 0.35,
                borderRadius: 1,
                bgcolor: 'rgba(12, 16, 22, 0.94)',
                color: '#F2F5F8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                pointerEvents: 'none',
              }}
            >
              {item.label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default RadialChoiceMenu;
